import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, Plus, XCircle, ChevronRight, ChevronLeft, Car } from 'lucide-react';
import { toast } from 'sonner';
import { PURPOSE_OPTIONS, getDays, calcTotalExpense } from './constants';

export const CreatePlanWizard = ({
  open, onOpenChange, form, setForm, step, setStep,
  locForm, setLocForm, places, addLocation, removeLocation,
  initExpenses, updateExpense, handleCreate,
}) => {
  const { api, selectedBusiness } = useApp();
  const [travelModes, setTravelModes] = useState([]);
  const totalExpense = calcTotalExpense(form.daily_expenses);

  useEffect(() => {
    if (open && selectedBusiness) {
      api.getTravelModes({ business_id: selectedBusiness }).then(res => {
        setTravelModes(res.data || []);
      }).catch(() => setTravelModes([]));
    }
  }, [open, selectedBusiness, api]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-plan-dialog" className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">New Travel Plan - Step {step} of 4</DialogTitle>
          <div className="flex gap-1 mt-2">{[1,2,3,4].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? 'bg-amber-500' : 'bg-slate-200'}`} />)}</div>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div><Label>Visit Type</Label>
              <Select value={form.visit_type} onValueChange={v => setForm({ ...form, visit_type: v })}>
                <SelectTrigger data-testid="visit-type-select"><SelectValue placeholder="Select visit type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_visit">Client Visit</SelectItem>
                  <SelectItem value="business_location">Business Location Visit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Purpose of Travel</Label>
              <Select value={form.purpose} onValueChange={v => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>{PURPOSE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Mode of Travel</Label>
              <Select value={form.mode_of_travel} onValueChange={v => setForm({ ...form, mode_of_travel: v })}>
                <SelectTrigger data-testid="travel-mode-select">
                  <SelectValue placeholder="Select travel mode" />
                </SelectTrigger>
                <SelectContent>
                  {travelModes.length > 0 ? (
                    travelModes.map(m => <SelectItem key={m.id} value={m.name}>{m.label}</SelectItem>)
                  ) : (
                    <SelectItem value="not_configured" disabled>No travel modes configured</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {travelModes.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">Ask admin to configure travel modes for this business line.</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input data-testid="start-date" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End Date</Label><Input data-testid="end-date" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} min={form.start_date} /></div>
            </div>
            <div>
              <Label>Estimated Total Distance (km)</Label>
              <Input 
                data-testid="total-distance" 
                type="number" 
                min={0}
                value={form.total_distance_km || ''} 
                onChange={e => setForm({ ...form, total_distance_km: parseFloat(e.target.value) || 0 })} 
                placeholder="Enter estimated distance"
              />
              <p className="text-xs text-slate-500 mt-1">This is used for fuel reimbursement calculation.</p>
            </div>
            {form.start_date && form.end_date && <p className="text-sm text-slate-500">Duration: {getDays(form.start_date, form.end_date)} day(s)</p>}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <h3 className="text-sm font-bold text-slate-700">Add Business Locations</h3>
            <div className="p-4 border border-border/60 rounded-lg space-y-3 bg-slate-50">
              <div><Label>Business Location</Label>
                <Select value={locForm.business_place_id} onValueChange={v => setLocForm({ ...locForm, business_place_id: v })}>
                  <SelectTrigger data-testid="location-select"><SelectValue placeholder="Select approved location" /></SelectTrigger>
                  <SelectContent>{places.filter(p => p.status === 'active').map(p => <SelectItem key={p.id} value={p.id}>{p.name} - {p.city}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Check-in Time</Label><Input data-testid="checkin-time" type="time" value={locForm.planned_checkin} onChange={e => setLocForm({ ...locForm, planned_checkin: e.target.value })} /></div>
                <div><Label>Check-out Time</Label><Input data-testid="checkout-time" type="time" value={locForm.planned_checkout} onChange={e => setLocForm({ ...locForm, planned_checkout: e.target.value })} /></div>
              </div>
              {locForm.business_place_id && (() => {
                const p = places.find(pl => pl.id === locForm.business_place_id);
                return p ? (
                  <div className="p-3 bg-white rounded-lg border border-border/40 text-xs text-slate-600">
                    <p className="font-semibold text-slate-800">{p.name}</p>
                    <p>{p.address}, {p.city} ({p.zone})</p>
                    <p>Contact: {p.contact_person} {p.contact_phone}</p>
                  </div>
                ) : null;
              })()}
              <Button data-testid="add-location-btn" onClick={addLocation} variant="outline" className="w-full gap-2"><Plus className="w-4 h-4" /> Add Location</Button>
            </div>
            {form.locations.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Locations ({form.locations.length})</Label>
                {form.locations.map((loc, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-border/40 rounded-lg bg-white">
                    <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{loc.place_name}</p>
                      <p className="text-xs text-slate-400">{loc.planned_checkin} - {loc.planned_checkout} ({loc.planned_duration_mins} mins)</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeLocation(i)}><XCircle className="w-4 h-4 text-red-400" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-4">
            <h3 className="text-sm font-bold text-slate-700">Daily Expenses (Rs.)</h3>
            <div className="space-y-3">
              {form.daily_expenses.map((exp, i) => (
                <div key={i} className="p-3 border border-border/40 rounded-lg bg-white">
                  <p className="text-xs font-bold text-slate-700 mb-2">Day {exp.day} - {exp.date}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div><Label className="text-[10px]">Breakfast</Label><Input type="number" value={exp.breakfast} onChange={e => updateExpense(i, 'breakfast', e.target.value)} className="h-8 text-sm" /></div>
                    <div><Label className="text-[10px]">Lunch</Label><Input type="number" value={exp.lunch} onChange={e => updateExpense(i, 'lunch', e.target.value)} className="h-8 text-sm" /></div>
                    <div><Label className="text-[10px]">Dinner</Label><Input type="number" value={exp.dinner} onChange={e => updateExpense(i, 'dinner', e.target.value)} className="h-8 text-sm" /></div>
                    {exp.night_stay >= 0 && <div><Label className="text-[10px]">Night Stay</Label><Input type="number" value={exp.night_stay} onChange={e => updateExpense(i, 'night_stay', e.target.value)} className="h-8 text-sm" /></div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right"><span className="text-sm font-bold text-slate-900">Total: Rs.{totalExpense.toLocaleString()}</span></div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 py-4">
            <h3 className="text-sm font-bold text-slate-700">Review Your Plan</h3>
            <div className="p-4 bg-slate-50 rounded-lg space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Type:</span><span className="font-semibold">{form.visit_type === 'client_visit' ? 'Client Visit' : 'Business Location Visit'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Purpose:</span><span className="font-semibold">{PURPOSE_OPTIONS.find(o => o.value === form.purpose)?.label}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Dates:</span><span className="font-semibold">{form.start_date} to {form.end_date} ({getDays(form.start_date, form.end_date)} days)</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Travel Mode:</span><span className="font-semibold flex items-center gap-1"><Car className="w-3.5 h-3.5" />{travelModes.find(m => m.name === form.mode_of_travel)?.label || form.mode_of_travel || 'Not set'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Distance:</span><span className="font-semibold">{form.total_distance_km || 0} km</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Locations:</span><span className="font-semibold">{form.locations.length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total Expense:</span><span className="font-bold text-amber-600">Rs.{totalExpense.toLocaleString()}</span></div>
            </div>
            <div>
              <Label className="mb-1.5 block">Locations</Label>
              {form.locations.map((loc, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border-b border-slate-100 text-sm">
                  <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <span className="font-semibold">{loc.place_name}</span>
                  <span className="text-slate-400 ml-auto">{loc.planned_checkin}-{loc.planned_checkout}</span>
                </div>
              ))}
            </div>
            <div><Label>Additional Notes</Label><Textarea data-testid="plan-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional details..." /></div>
          </div>
        )}

        <DialogFooter className="flex justify-between">
          <div>{step > 1 && <Button variant="outline" onClick={() => setStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            {step < 4 ? (
              <Button data-testid="next-step-btn" onClick={() => {
                if (step === 1 && (!form.visit_type || !form.purpose || !form.start_date || !form.end_date)) { toast.error('Fill all fields'); return; }
                if (step === 2 && form.locations.length === 0) { toast.error('Add at least one location'); return; }
                if (step === 2) initExpenses();
                setStep(s => s + 1);
              }} className="bg-amber-600 hover:bg-amber-700 text-white">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button data-testid="submit-plan-btn" onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700 text-white">Submit for Approval</Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
